import { h, on, empty } from '../../../../../src/shared/components/dom.js';
import { Chip } from '../../../../../src/shared/components/chip.js';
import { Spinner } from '../../../../../src/shared/components/spinner.js';
import { EmptyState } from '../../../../../src/shared/components/empty-state.js';
import { setPageTitle } from '../../../../../src/shared/page-title.js';
import { badgeIconEl } from '../../../../../src/shared/components/badge-icons.js';
import { LibraryGridTile as GridTile, trendingGameHref } from '../../components/library-grid-tile.js';
import { FilterSidebar } from './filter-sidebar.js';
import { openUploadRomModal } from '../../components/upload-rom-modal.js';
import { state } from '../../state.js';
import { getCatalog, getTrendingGames, deleteUserRom } from '../../api.js';
import { navigate } from '../../router.js';
import { LIBRARY_SYSTEMS, systemName, enabledLibrarySystems } from '../../shared/system-meta.js';
import { canUsePanel, isDetailPanelOpen, closeDetailPanel } from '../../components/detail-panel.js';
import { openGameReport } from '../../../../../src/shared/components/report-game-panel.js';
import { planRomRemoval } from './rom-removal.js';
import { createPageRetryGate } from '../page-retry-gate.js';
import { libraryPlayUrl } from '../../play-url.js';
import { releaseTypesParam, includedHiddenTypes, hasNarrowingFilters, sanitizeReleaseTypes } from '../../browse-state.js';

/**
 * Main library grid screen (Phase 1 version).
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────┐
 *   │  [search]  [sort ▾]  [view: grid | list]                 │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  All  NES  SNES  GB  GBA  Genesis  Atari 2600  C64       │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  [tile] [tile] [tile] [tile] [tile] ...                  │
 *   └──────────────────────────────────────────────────────────┘
 *
 * Phase 1 reads from /api/library/catalog which joins /api/roms data with
 * the `games` table. Artwork, description, year, genre surface automatically
 * once the ingestion pipeline populates those columns — no client change
 * needed.
 */
// Cache the index screen's DOM tree so navigating back from a game
// detail page restores the grid + scroll position instantly instead
// of tearing down and re-fetching. The cache is invalidated when the
// user changes a filter, sorts, or searches (reloadCatalog clears it).
let _cachedScreen = null;
let _cachedSetupObserver = null;
let _filterDrawerBackdrop = null;
let _closeFilterDrawer = null;

export function invalidateCache() {
  _closeFilterDrawer?.({ restoreFocus: false });
  _closeFilterDrawer = null;
  _cachedScreen = null;
  _cachedSetupObserver = null;
  if (_filterDrawerBackdrop) { _filterDrawerBackdrop.remove(); _filterDrawerBackdrop = null; }
}
let _lastClickedTile = null; // DOM element of the tile the user clicked

// New-UI gate: kill switch (=== false only — the flag defaults ON) + auth.
function myRomsUiEnabled() {
  const { features, user } = state.get();
  return features?.library_my_roms !== false && !!user;
}

export function render(_params, { root, isCurrent = () => true }) {
  if (isDetailPanelOpen()) closeDetailPanel({ skipCallback: true });
  empty(root);

  // If we have a cached DOM tree (coming back from a detail page),
  // restore it and the scroll position without re-fetching. The DOM
  // contains all tiles that were rendered before the user navigated
  // away. If more items remain unrendered, the initial load at the
  // bottom of this function will be skipped (already ran), but we
  // re-setup the infinite scroll observer so the user can keep scrolling.
  if (_cachedScreen) {
    root.appendChild(_cachedScreen);
    // Scroll back to the tile the user clicked. Using scrollIntoView on
    // the actual DOM element instead of a pixel offset — this works even
    // though lazy-loaded images above haven't resolved their heights yet
    // (the element exists in the DOM, the browser finds it by reference).
    if (_lastClickedTile) {
      requestAnimationFrame(() => {
        _lastClickedTile.scrollIntoView({ block: 'center', behavior: 'instant' });
      });
    }
    // Re-setup the observer if there are still unrendered items.
    if (_cachedSetupObserver) _cachedSetupObserver();
    return true;
  }

  const screen = h('section', { class: 'lib-index-screen' });
  screen.appendChild(
    h('h1', { id: 'library-title', class: 'lib-visually-hidden' }, 'Game Library')
  );

  // ── Top bar (search / sort / view) ───────────────────────────────
  const searchInput = h('input', {
    type: 'search',
    class: 'lib-search',
    placeholder: 'Search your library…',
    value: state.get().catalog.search || '',
    'aria-label': 'Search library',
  });
  // Debounce the search input so we don't fire a request per keystroke.
  // 200ms feels responsive without chattering — on typical typing speed
  // (~5 chars/sec) it coalesces a word into one request, and a short
  // pause fires immediately. Each new keystroke clears the pending
  // timer + aborts any in-flight fetch via reloadCatalog().
  let searchDebounceTimer = null;
  on(searchInput, 'input', (e) => {
    const val = e.target.value;
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = setTimeout(() => {
      state.update('catalog.search', val);
      reloadCatalog();
    }, 200);
  });

  const sortSelect = h('select', { class: 'lib-select', 'aria-label': 'Sort order' },
    h('option', { value: 'title' }, 'A–Z'),
    h('option', { value: 'recent' }, 'Recently played'),
    h('option', { value: 'favorites' }, 'Favorites first'),
    h('option', { value: 'rating' }, 'Highest rated'),
  );
  sortSelect.value = state.get().catalog.sort;
  on(sortSelect, 'change', (e) => {
    state.update('catalog.sort', e.target.value);
    reloadCatalog();
  });

  const viewToggle = h('div', { class: 'lib-view-toggle', role: 'group', 'aria-label': 'View mode' });
  const gridBtn = h('button', {
    type: 'button',
    class: 'lib-view-btn',
    'aria-pressed': state.get().catalog.view === 'grid' ? 'true' : 'false',
    title: 'Grid view',
  }, '▦');
  const listBtn = h('button', {
    type: 'button',
    class: 'lib-view-btn',
    'aria-pressed': state.get().catalog.view === 'list' ? 'true' : 'false',
    title: 'List view',
  }, '☰');
  on(gridBtn, 'click', () => { state.update('catalog.view', 'grid'); syncViewToggle(); renderResults(); });
  on(listBtn, 'click', () => { state.update('catalog.view', 'list'); syncViewToggle(); renderResults(); });
  viewToggle.append(gridBtn, listBtn);

  function syncViewToggle() {
    gridBtn.setAttribute('aria-pressed', state.get().catalog.view === 'grid' ? 'true' : 'false');
    listBtn.setAttribute('aria-pressed', state.get().catalog.view === 'list' ? 'true' : 'false');
  }

  const uploadBtn = myRomsUiEnabled()
    ? h('button', { type: 'button', class: 'lib-btn lib-btn-ghost lib-upload-btn', title: 'Upload ROMs' },
        h('span', { class: 'lib-btn-glyph', 'aria-hidden': 'true' }, '⬆'),
        ' Upload ROMs')
    : null;
  if (uploadBtn) {
    on(uploadBtn, 'click', () => openUploadRomModal({
      defaultSystem: state.get().catalog.system,
      onDone: (n) => { if (n) reloadCatalog(); },
    }));
  }

  const filterToggleBtn = h('button', {
    type: 'button',
    class: 'lib-btn lib-btn-ghost lib-filter-drawer-toggle',
    'aria-label': 'Open filters',
    title: 'Filters',
  }, '☰ Filters');

  const topbar = h('div', { class: 'lib-index-topbar' },
    searchInput,
    h('div', { class: 'lib-index-topbar-controls' },
      uploadBtn,
      filterToggleBtn,
      h('label', { class: 'lib-control' },
        h('span', { class: 'lib-control-label' }, 'Sort'),
        sortSelect,
      ),
      viewToggle,
    ),
  );
  screen.appendChild(topbar);

  // ── System filter chips ──────────────────────────────────────────
  const chipsRow = h('div', { class: 'lib-system-chips', role: 'group', 'aria-label': 'Filter by system' });
  const activeSystem = state.get().catalog.system || 'all';
  const visibleSystems = enabledLibrarySystems(state.get().features);
  chipsRow.appendChild(Chip({
    label: 'All',
    active: activeSystem === 'all',
    onClick: () => { state.update('catalog.system', 'all'); reloadCatalog(); rerenderChips(); },
  }));
  for (const sys of visibleSystems) {
    chipsRow.appendChild(Chip({
      label: sys.name,
      active: activeSystem === sys.id,
      onClick: () => { state.update('catalog.system', sys.id); reloadCatalog(); rerenderChips(); },
    }));
  }
  screen.appendChild(chipsRow);

  // ── Trending strip (gated on discovery flag) ─────────────────
  const trendingSection = h('section', { class: 'lib-trending-section' });
  const { features, prefs } = state.get();
  if (features?.discovery !== false && prefs?.discovery?.trending !== false) {
  screen.appendChild(trendingSection);

  // Load async — don't block initial render
  getTrendingGames({ limit: 12 }).then(({ games }) => {
    if (!isCurrent()) return;
    if (!games || !games.length) return;

    trendingSection.appendChild(
      h('h2', { class: 'lib-trending-title' }, 'Trending This Week')
    );

    const strip = h('div', { class: 'lib-trending-strip' });
    for (const game of games) {
      const href = trendingGameHref(game);
      const tile = h('a', {
        class: 'lib-trending-tile',
        href,
        title: game.title,
      },
        h('img', {
          class: 'lib-trending-cover',
          src: game.cover_url || `/api/library/artwork/cover/${game.game_id}`,
          alt: '',
          loading: 'lazy',
          onerror: 'this.style.display="none"',
        }),
        h('div', { class: 'lib-trending-info' },
          h('span', { class: 'lib-trending-name' }, game.title || 'Unknown'),
          h('span', { class: 'lib-trending-meta' },
            `${game.unique_players} player${game.unique_players !== 1 ? 's' : ''}`),
        ),
      );

      // Navigate via library router instead of full page load
      tile.addEventListener('click', (e) => {
        e.preventDefault();
        if (href) navigate(href);
      });

      strip.appendChild(tile);
    }
    trendingSection.appendChild(strip);
  });
  }

  function rerenderChips() {
    empty(chipsRow);
    const s = state.get().catalog.system || 'all';
    chipsRow.appendChild(Chip({
      label: 'All',
      active: s === 'all',
      onClick: () => { state.update('catalog.system', 'all'); reloadCatalog(); rerenderChips(); rerenderSidebar(); },
    }));
    for (const sys of enabledLibrarySystems(state.get().features)) {
      chipsRow.appendChild(Chip({
        label: sys.name,
        active: s === sys.id,
        onClick: () => { state.update('catalog.system', sys.id); reloadCatalog(); rerenderChips(); rerenderSidebar(); },
      }));
    }
  }

  // ── Sidebar + results in a 2-column layout ───────────────────────
  const sidebarContainer = h('div', { class: 'lib-sidebar-slot' });
  const resultsSection = h('div', { class: 'lib-results' });
  const resultsHeading = h('h2', { class: 'lib-results-heading', tabindex: '-1' }, 'Results');
  const hiddenHint = h('p', { class: 'lib-results-hidden-hint', role: 'status', 'aria-live': 'polite' });
  const statusLine = h('p', { class: 'lib-results-status', 'aria-live': 'polite' });
  const featureSummary = h('p', { class: 'lib-results-features' });
  const resultsBody = h('div', { class: 'lib-results-body' });
  // Dedicated announce region for removal outcomes. statusLine gets
  // rewritten by every reloadCatalog() call (which removal triggers), so an
  // announcement written there would be clobbered before screen readers
  // pick it up — this region only ever holds the removal result.
  const resultsAnnounce = h('p', { class: 'lib-visually-hidden', role: 'status', 'aria-live': 'polite' });
  resultsSection.append(resultsHeading, statusLine, featureSummary, hiddenHint, resultsAnnounce, resultsBody);

  const layout = h('div', { class: 'lib-index-layout' },
    sidebarContainer,
    resultsSection,
  );
  screen.appendChild(layout);

  // ── Sidebar (genre chips, toggles, alphabet nav) ────────────────
  function rerenderSidebar() {
    const { catalog } = state.get();
    const filters = catalog.filters || { genre: '', favorite: false, played: undefined, letter: '' };
    empty(sidebarContainer);
    sidebarContainer.appendChild(FilterSidebar({
      filters,
      system: catalog.system || 'all',
      showMyRoms: true,
      onChange: (newFilters, focusType) => {
        state.update('catalog.filters', newFilters);
        reloadCatalog();
        rerenderSidebar();
        if (focusType) sidebarContainer.querySelector(`[data-release-control="${focusType}"]`)?.focus();
      },
    }));
  }
  rerenderSidebar();

  // ── Filter drawer (mobile overlay) ─────────────────────────────
  let _filterDrawerEscHandler = null;
  let _filterDrawerResizeHandler = null;

  function renderDrawerSidebar(body) {
    empty(body);
    const { catalog } = state.get();
    const filters = catalog.filters || { genre: '', favorite: false, played: undefined, letter: '' };
    body.appendChild(FilterSidebar({
      filters,
      system: catalog.system || 'all',
      skipLiveNow: true,
      showMyRoms: true,
      onChange: (newFilters, focusType) => {
        state.update('catalog.filters', newFilters);
        reloadCatalog();
        rerenderSidebar();
        renderDrawerSidebar(body);
        if (focusType) body.querySelector(`[data-release-control="${focusType}"]`)?.focus();
      },
    }));
  }

  function openFilterDrawer() {
    if (!_filterDrawerBackdrop) {
      const closeBtn = h('button', {
        type: 'button',
        class: 'lib-filter-drawer-close',
        'aria-label': 'Close filters',
      }, '×');
      on(closeBtn, 'click', closeFilterDrawer);

      const drawerBody = h('div', { class: 'lib-filter-drawer-body' });
      const drawer = h('aside', {
        class: 'lib-filter-drawer',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': 'Filters',
      },
        h('div', { class: 'lib-filter-drawer-header' },
          h('span', { class: 'lib-filter-drawer-title' }, 'Filters'),
          closeBtn,
        ),
        drawerBody,
      );

      _filterDrawerBackdrop = h('div', {
        class: 'lib-filter-drawer-backdrop', inert: true, 'aria-hidden': 'true',
      }, drawer);
      on(_filterDrawerBackdrop, 'click', (e) => {
        if (e.target === _filterDrawerBackdrop) closeFilterDrawer();
        if (e.target.closest('a[href]')) closeFilterDrawer({ restoreFocus: false });
      });
      document.body.appendChild(_filterDrawerBackdrop);
    }

    const body = _filterDrawerBackdrop.querySelector('.lib-filter-drawer-body');
    renderDrawerSidebar(body);

    _filterDrawerBackdrop.removeAttribute('inert');
    _filterDrawerBackdrop.removeAttribute('aria-hidden');
    _filterDrawerBackdrop.classList.add('open');
    screen.setAttribute('inert', '');
    document.body.style.overflow = 'hidden';
    _filterDrawerBackdrop.querySelector('.lib-filter-drawer-close')?.focus();

    _filterDrawerEscHandler = (e) => {
      if (e.key === 'Escape') { closeFilterDrawer(); return; }
      if (e.key !== 'Tab') return;
      const controls = [..._filterDrawerBackdrop.querySelectorAll('a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])')];
      if (!controls.length) return;
      if (e.shiftKey && document.activeElement === controls[0]) {
        e.preventDefault(); controls.at(-1).focus();
      } else if (!e.shiftKey && document.activeElement === controls.at(-1)) {
        e.preventDefault(); controls[0].focus();
      }
    };
    document.addEventListener('keydown', _filterDrawerEscHandler);

    _filterDrawerResizeHandler = () => { if (window.innerWidth > 900) closeFilterDrawer({ restoreFocus: false }); };
    window.addEventListener('resize', _filterDrawerResizeHandler);
  }

  function closeFilterDrawer({ restoreFocus = true } = {}) {
    if (!_filterDrawerBackdrop?.classList.contains('open')) return;
    _filterDrawerBackdrop.classList.remove('open');
    _filterDrawerBackdrop.setAttribute('inert', '');
    _filterDrawerBackdrop.setAttribute('aria-hidden', 'true');
    screen.removeAttribute('inert');
    document.body.style.overflow = '';
    if (_filterDrawerEscHandler) {
      document.removeEventListener('keydown', _filterDrawerEscHandler);
      _filterDrawerEscHandler = null;
    }
    if (_filterDrawerResizeHandler) {
      window.removeEventListener('resize', _filterDrawerResizeHandler);
      _filterDrawerResizeHandler = null;
    }
    if (restoreFocus && filterToggleBtn.isConnected) filterToggleBtn.focus();
  }
  _closeFilterDrawer = closeFilterDrawer;

  on(filterToggleBtn, 'click', openFilterDrawer);

  root.appendChild(screen);

  // Drag-and-drop upload (v1 homepage parity — any ROM dropped on the page).
  let dragDepth = 0;
  on(screen, 'dragover', (e) => { if (myRomsUiEnabled()) e.preventDefault(); });
  on(screen, 'dragenter', (e) => {
    if (!myRomsUiEnabled()) return;
    e.preventDefault();
    dragDepth++;
    screen.classList.add('lib-drop-active');
  });
  on(screen, 'dragleave', () => {
    if (dragDepth > 0 && --dragDepth === 0) screen.classList.remove('lib-drop-active');
  });
  on(screen, 'drop', (e) => {
    if (!myRomsUiEnabled()) return;
    e.preventDefault();
    dragDepth = 0;
    screen.classList.remove('lib-drop-active');
    const files = [...(e.dataTransfer?.files || [])];
    if (files.length) {
      openUploadRomModal({
        files,
        defaultSystem: state.get().catalog.system,
        onDone: (n) => { if (n) reloadCatalog(); },
      });
    }
  });

  // ── Render / reload logic (infinite scroll) ──────────────────────
  const PAGE_SIZE = 100;
  let lastToken = 0;
  let allItems = [];       // pages loaded so far
  let renderedCount = 0;   // how many tiles are in the DOM so far
  let catalogTotal = 0;    // total matching items (server-side)
  let nextOffset = 0;
  let catalogHasMore = false;
  let gridContainer = null;
  let sentinel = null;     // IntersectionObserver target at the bottom
  let observer = null;
  const pageRetry = createPageRetryGate();

  // AbortController for the catalog fetch. Each call to reloadCatalog
  // aborts the previous one so a rapid filter/search change cancels
  // the in-flight request at the network layer instead of just
  // discarding its response — this frees server CPU and keeps the
  // browser's request queue clean for the actual desired query.
  let currentAbort = null;
  let loadMoreAbort = null;

  function catalogQuery(offset) {
    const { catalog } = state.get();
    const filters = catalog.filters || {};
    return {
      system: catalog.system && catalog.system !== 'all' ? catalog.system : undefined,
      search: catalog.search || undefined,
      sort: catalog.sort,
      genre: filters.genre || undefined,
      tag: filters.tag || undefined,
      favorite: filters.favorite ? 'true' : undefined,
      mine: filters.mine ? '1' : undefined,
      played: filters.played || undefined,
      letter: filters.letter || undefined,
      filter_id: filters.filter_id || undefined,
      top100: filters.top100 ? 'true' : undefined,
      achievements: filters.achievements ? 'true' : undefined,
      scores: filters.scores ? 'true' : undefined,
      release_types: releaseTypesParam(filters, state.get().features),
      limit: PAGE_SIZE,
      offset,
    };
  }

  async function reloadCatalog({ initial = false } = {}) {
    const token = ++lastToken;

    // Abort any still-running fetch. The await below catches the
    // AbortError and returns silently.
    if (currentAbort) currentAbort.abort();
    if (loadMoreAbort) loadMoreAbort.abort();
    loadMoreAbort = null;
    currentAbort = new AbortController();
    const signal = currentAbort.signal;

    empty(resultsBody);
    resultsBody.appendChild(Spinner({ label: 'Loading library…' }));
    statusLine.textContent = '';
    featureSummary.textContent = '';
    allItems = [];
    renderedCount = 0;
    catalogTotal = 0;
    nextOffset = 0;
    catalogHasMore = false;
    pageRetry.reset();
    destroyObserver();
    hiddenHint.replaceChildren();
    hiddenHint.classList.remove('lib-visually-hidden');

    if (releaseTypesParam(state.get().catalog.filters, state.get().features) === null) {
      hiddenHint.classList.add('lib-visually-hidden');
      hiddenHint.textContent = 'Choose a release type to see games.';
      state.set({ catalog: { ...state.get().catalog, roms: [] } });
      empty(resultsBody);
      resultsBody.appendChild(EmptyState({
        title: 'Choose a release type to see games.',
        action: h('button', { type: 'button', class: 'lib-btn', onclick: () => {
          state.update('catalog.filters', {
            ...state.get().catalog.filters, release_types: ['official'],
          });
          rerenderSidebar();
          reloadCatalog();
          resultsHeading.focus();
        } }, 'Show official releases'),
      }));
      _cachedScreen = screen;
      return true;
    }

    try {
      const data = await getCatalog(catalogQuery(0), { signal });
      if (token !== lastToken) return false;
      if (!isCurrent()) return false;
      allItems = data.items || [];
      catalogTotal = Number(data.total) || 0;
      nextOffset = data.next_offset == null ? allItems.length : Number(data.next_offset);
      catalogHasMore = data.has_more === true;
      renderHiddenHint(data.hidden_by_release_type);
      state.set({ catalog: { ...state.get().catalog, roms: allItems } });
      renderResults();
      // A failed/superseded initial request clears the cache below. A later
      // explicit Retry that succeeds promotes this same rendered screen back
      // into the fast-return cache.
      _cachedScreen = screen;
      return true;
    } catch (err) {
      // Aborts are expected — swallow silently. The caller that
      // triggered the abort is about to render its own spinner.
      if (err?.name === 'AbortError' || token !== lastToken || !isCurrent()) {
        if (initial && _cachedScreen === screen) _cachedScreen = null;
        return false;
      }
      empty(resultsBody);
      hiddenHint.replaceChildren();
      resultsBody.appendChild(EmptyState({
        title: 'Couldn\'t load the library',
        message: err?.body?.error === 'not_found'
          ? 'The library module is disabled. Ask an admin to enable it in /admin.'
          : err?.body?.error === 'catalog_user_enrichment_unavailable'
            ? 'Your favorites or play history are temporarily unavailable. No cached zeroes were shown.'
            : 'The server returned an error. Try again in a moment.',
        action: h('button', { type: 'button', class: 'lib-btn', onclick: reloadCatalog }, 'Retry'),
      }));
      if (initial && _cachedScreen === screen) _cachedScreen = null;
      return false;
    }
  }

  async function loadMoreCatalog({ explicitRetry = false } = {}) {
    if (!catalogHasMore || releaseTypesParam(state.get().catalog.filters, state.get().features) === null) return;
    const lease = pageRetry.start({ explicit: explicitRetry });
    if (!lease) return;
    const token = lastToken;
    const controller = new AbortController();
    loadMoreAbort = controller;
    if (explicitRetry) statusLine.textContent = 'Retrying…';
    try {
      const data = await getCatalog(catalogQuery(nextOffset), { signal: controller.signal });
      if (token !== lastToken || !pageRetry.owns(lease)) return;
      const page = data.items || [];
      allItems.push(...page);
      catalogTotal = Number(data.total) || catalogTotal;
      nextOffset = data.next_offset == null ? allItems.length : Number(data.next_offset);
      catalogHasMore = data.has_more === true;
      pageRetry.succeed(lease);
      state.set({ catalog: { ...state.get().catalog, roms: allItems } });
      appendNextPage();
      updateCatalogStatus();
      updateFeatureSummary();
    } catch (err) {
      if (err?.name !== 'AbortError' && token === lastToken && pageRetry.owns(lease)) {
        hiddenHint.replaceChildren();
        pageRetry.fail(lease);
        statusLine.replaceChildren(
          document.createTextNode('Couldn\'t load more games. '),
          h('button', {
            type: 'button', class: 'lib-btn',
            onclick: () => loadMoreCatalog({ explicitRetry: true }),
          }, 'Retry'),
        );
      }
    } finally {
      if (loadMoreAbort === controller) loadMoreAbort = null;
      const ownedCurrentGeneration = pageRetry.finish(lease);
      if (ownedCurrentGeneration && token === lastToken && pageRetry.shouldRearm(catalogHasMore)) {
        setupObserver();
      }
    }
  }

  function updateCatalogStatus() {
    const filterPhrase = describeFilter();
    const shown = catalogTotal || allItems.length;
    statusLine.textContent = `${shown.toLocaleString()} game${shown === 1 ? '' : 's'} · ${filterPhrase}`;
  }

  function renderResults() {
    empty(resultsBody);
    renderedCount = 0;
    destroyObserver();

    if (allItems.length === 0) {
      if (hiddenHint.textContent) {
        statusLine.textContent = '';
        featureSummary.textContent = '';
        return;
      }
      const mineActive = !!state.get().catalog.filters?.mine;
      resultsBody.appendChild(EmptyState(mineActive ? {
        title: 'No ROMs of your own yet',
        message: state.get().user
          ? 'Use "⬆ Upload ROMs" above. Games already in RetroPlay are recognized instantly and use no storage.'
          : 'Sign in to upload ROMs and see your own library here.',
      } : {
        title: 'Nothing to show yet',
        message: 'Drop ROM files in your roms/ directory to populate the library, or adjust filters above.',
      }));
      statusLine.textContent = '';
      featureSummary.textContent = '';
      return;
    }

    // Use the server-reported total, not allItems.length, so the
    // count stays honest even if the limit cap clips the grid.
    updateCatalogStatus();
    updateFeatureSummary();

    const view = state.get().catalog.view;
    gridContainer = h('div', { class: view === 'list' ? 'lib-grid lib-grid-list' : 'lib-grid lib-grid-tiles' });
    resultsBody.appendChild(gridContainer);

    // Render the first page immediately.
    appendNextPage();

    // If there are more items, set up the IntersectionObserver sentinel.
    if (pageRetry.shouldRearm(catalogHasMore)) {
      setupObserver();
    }
  }

  function renderHiddenHint(hidden) {
    hiddenHint.replaceChildren();
    hiddenHint.classList.remove('lib-visually-hidden');
    if (state.get().features?.library_release_types !== true) return;
    const { search, filters } = state.get().catalog;
    const types = includedHiddenTypes(filters?.release_types, hidden);
    if (!types.length || (allItems.length > 0 && !hasNarrowingFilters(search, filters))) return;
    const count = Number(hidden.total);
    hiddenHint.append(
      document.createTextNode(`${count} more match${count === 1 ? '' : 'es'} ${count === 1 ? 'is' : 'are'} hidden by release type. `),
      h('button', { type: 'button', class: 'lib-btn', onclick: () => {
        state.update('catalog.filters', {
          ...state.get().catalog.filters,
          release_types: [...sanitizeReleaseTypes(state.get().catalog.filters.release_types), ...types],
        });
        rerenderSidebar();
        reloadCatalog();
        resultsHeading.focus();
      } }, 'Include them'),
    );
  }

  function appendNextPage() {
    const end = Math.min(renderedCount + PAGE_SIZE, allItems.length);
    const mineActive = !!state.get().catalog.filters?.mine;
    for (let i = renderedCount; i < end; i++) {
      const item = allItems[i];
      gridContainer.appendChild(GridTile({
        rom: item,
        onPlay: launchGame,
        onDetail: openDetail,
        onReport: openReport,
        // Spec Goal 4: uploads are removable everywhere they render; claims
        // (owned catalog rows) are removable in the My ROMs management view.
        onRemove: (item.user_rom || (mineActive && item.owned)) ? removeUserRom : undefined,
      }));
    }
    renderedCount = end;

    // If we've rendered everything, remove the sentinel.
    if (renderedCount >= allItems.length && !catalogHasMore) {
      destroyObserver();
    }
  }

  function setupObserver() {
    destroyObserver();
    sentinel = h('div', { class: 'lib-scroll-sentinel', 'aria-hidden': 'true' });
    sentinel.appendChild(Spinner({ label: 'Loading more…' }));
    resultsBody.appendChild(sentinel);

    observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !pageRetry.loading && catalogHasMore) {
          destroyObserver();
          loadMoreCatalog();
        }
      }
    }, { rootMargin: '400px' });
    observer.observe(sentinel);
  }

  function destroyObserver() {
    if (observer) { observer.disconnect(); observer = null; }
    if (sentinel) { sentinel.remove(); sentinel = null; }
  }

  // Expose setupObserver at module scope so the cache-restore path can
  // re-arm infinite scroll without re-running the full render.
  _cachedSetupObserver = () => {
    if (pageRetry.shouldRearm(catalogHasMore)) setupObserver();
  };

  function describeFilter() {
    const { catalog } = state.get();
    const bits = [];
    if (catalog.system && catalog.system !== 'all') bits.push(systemName(catalog.system));
    if (catalog.search) bits.push(`matching "${catalog.search}"`);
    if (!bits.length) bits.push('all systems');
    bits.push(`sorted by ${catalog.sort === 'title' ? 'name' : catalog.sort}`);
    return bits.join(' · ');
  }

  function updateFeatureSummary() {
    const { catalog } = state.get();
    const bits = [];
    if (allItems.length && catalog.system && catalog.system !== 'all') {
      const withAch = allItems.filter(it => (it.achievement_count || 0) > 0).length;
      const withScores = allItems.filter(it => it.score_supported).length;
      if (withAch) bits.push({ icon: 'achievements', text: `${withAch} with achievements` });
      if (withScores) bits.push({ icon: 'score', text: `${withScores} with high scores` });
    }
    // Search-results honesty sub-line (homepage v2 spec §3.4): name what the
    // grid can't show at a glance — locked BYO entries and alias matches.
    if (allItems.length && catalog.search) {
      const locked = allItems.filter(it => it.playable === false).length;
      const aliasHits = allItems.filter(it => it.alias_matched === true).length;
      if (locked) bits.push({ text: `\u{1F512} ${locked} need your own ROM` });
      if (aliasHits) bits.push({ text: `${aliasHits} matched an alias` });
    }
    // Built from nodes rather than a joined string so the two feature bits can
    // carry the real badge icons instead of emoji. Text still goes through text
    // nodes, so nothing here is parsed as markup.
    featureSummary.replaceChildren(...bits.flatMap((bit, i) => {
      const parts = i ? [document.createTextNode(' · ')] : [];
      if (bit.icon) {
        parts.push(badgeIconEl(bit.icon), document.createTextNode(` ${bit.text}`));
      } else {
        parts.push(document.createTextNode(bit.text));
      }
      return parts;
    }));
  }

  // Launch the main app with deep-link params. Bundled rows use the Phase-1
  // ?system=&rom= contract; synthetic "My ROMs" rows aren't bundled files,
  // so they launch by content hash via ?myrom= (main.js boot handler).
  function launchGame(rom) {
    location.assign(libraryPlayUrl(rom.system, rom.user_rom ? rom.hash : rom.name, {
      myRom: !!rom.user_rom,
      solo: false,
    }));
  }

  // Remove an owned row (upload or claim). Confirmation first — a hover-bar
  // ✕ is one misclick from destroying an upload — then announce the outcome
  // through the dedicated polite region (reloadCatalog rewrites statusLine).
  // ownership_system targets the actual user_roms row (a gbc-filed claim
  // can surface under the gb chip). Deletion copy only when a stored blob
  // actually exists: claims (base tiles and claim synthetics, stale or
  // not) store no uploaded copy.
  async function removeUserRom(rom) {
    const plan = planRomRemoval(rom);
    if (!window.confirm(plan.confirmMessage)) return;
    try {
      await deleteUserRom(plan.system, rom.hash);
      await reloadCatalog();
      resultsAnnounce.textContent = plan.successMessage;
    } catch (err) {
      resultsAnnounce.textContent = plan.failureMessage;
      console.warn('[library] remove user rom failed:', err);
    }
  }

  function openReport(rom) {
    openGameReport({
      game_id: rom.game_id != null ? Number(rom.game_id) : null,
      system_id: rom.system,
      rom_hash: rom.hash || null,
      rom_filename: rom.name || null,
      game_title: rom.displayName || rom.name || null,
    });
  }

  // Navigate to the game detail page. Save a reference to the clicked
  // tile element so we can scrollIntoView on back navigation.
  //
  // Uses the game row's canonical `game_system` for the URL when the
  // catalog surfaces it — otherwise a GBC game browsed through the GB
  // catalog would navigate to /library/g/gb/<slug> and 404 because the
  // games row is filed under gbc. Falls back to the ROM's listed
  // system when the enrichment join didn't return a game_system
  // (unmatched ROMs, or ROMs in categories without aliasing).
  function openDetail(rom, tileEl) {
    closeFilterDrawer({ restoreFocus: false });
    const slug = rom.game_slug || rom.game_id || rom.hash;
    if (!slug) return launchGame(rom);
    const sys = rom.game_system || rom.system;
    _lastClickedTile = tileEl || null;

    const usePanel = canUsePanel();
    navigate(`/library/g/${sys}/${slug}`, {
      // Detail panels replace the catalog URL and preserve its scroll; full
      // pages keep the normal push + scroll-to-top navigation behavior.
      replace: usePanel,
      scroll: !usePanel,
    });
  }

  // Initial load.
  return reloadCatalog({ initial: true });
}
